import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { format, parseISO, isToday } from 'date-fns'
import { ru } from 'date-fns/locale'
import PairActivity from '@/components/client/PairActivity'
import Button from '@/components/ui/Button'
import {
  getMyBookings,
  cancelBooking,
  sendCalendarToTelegram,
  Booking,
} from '@/services/bookings'
import {
  getMyTrainingPlans,
  TrainingPlan,
  Exercise,
} from '@/services/trainingPlans'
import { getMyTrainers } from '@/services/clients'

type TabType = 'upcoming' | 'past' | 'plans'

export default function MyTrainingsPage() {
  const [bookings, setBookings] = useState<Booking[]>([])
  const [plans, setPlans] = useState<TrainingPlan[]>([])
  const [hasTrainer, setHasTrainer] = useState(false)
  const [trialState, setTrialState] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isPlansLoading, setIsPlansLoading] = useState(false)
  const [plansFullyLoaded, setPlansFullyLoaded] = useState(false)
  const [activeTab, setActiveTab] = useState<TabType>('upcoming')
  const [selectedPlan, setSelectedPlan] = useState<TrainingPlan | null>(null)

  const loadData = useCallback(async () => {
    setIsLoading(true)
    try {
      const [bookingsData, trainersData] = await Promise.all([
        getMyBookings(),
        getMyTrainers(),
      ])
      setBookings(bookingsData)
      // Проверяем есть ли активный тренер (approved, trial или frozen - frozen может записаться)
      const activeTrainers = trainersData.trainers.filter(
        (t) => t.status === 'approved' || t.status === 'trial' || t.status === 'frozen'
      )
      setHasTrainer(activeTrainers.length > 0)
      // Trial state первого активного тренера
      if (activeTrainers.length > 0 && activeTrainers[0].status === 'trial') {
        setTrialState(activeTrainers[0].trial_state || 'available')
      } else {
        setTrialState(null)
      }
    } catch (err) {
      console.error('Error loading data:', err)
    } finally {
      setIsLoading(false)
    }
    // Быстрая загрузка планов без картинок — только для счётчика
    loadPlans(false)
  }, [])

  useEffect(() => {
    loadData()
  }, [loadData])

  // Refetch when app becomes visible (Telegram Mini App caching)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        loadData()
      }
    }
    document.addEventListener('visibilitychange', handleVisibility)
    return () => document.removeEventListener('visibilitychange', handleVisibility)
  }, [loadData])

  // Догружаем полные данные при переходе на таб "Планы"
  useEffect(() => {
    if (activeTab === 'plans' && !plansFullyLoaded && plans.length > 0) {
      loadPlans(true)
    }
  }, [activeTab, plansFullyLoaded, plans.length])

  const loadBookings = async () => {
    try {
      const data = await getMyBookings()
      setBookings(data)
    } catch (err) {
      console.error('Error loading bookings:', err)
    }
  }

  const loadPlans = async (includeImages: boolean) => {
    setIsPlansLoading(true)
    try {
      const data = await getMyTrainingPlans(undefined, includeImages)
      setPlans(data.plans)
      setSelectedPlan(null)
      setPlansFullyLoaded(includeImages)
    } catch (err) {
      console.error('Error loading plans:', err)
    } finally {
      setIsPlansLoading(false)
    }
  }

  const handleCancel = async (bookingId: string) => {
    const paired = bookings.find(b => b.id === bookingId)?.pair_status === 'accepted'
    if (!confirm(paired ? 'Отменить парную тренировку для обоих участников?' : 'Отменить эту тренировку?')) return
    try {
      await cancelBooking(bookingId)
      loadBookings()
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Ошибка отмены')
    }
  }

  // Разделяем на предстоящие и прошедшие (учитываем время окончания тренировки)
  const now = new Date()

  const getSlotDateTime = (b: Booking) => {
    const slotDate = parseISO(b.slot.date)
    const [hours, minutes] = b.slot.start_time.split(':').map(Number)
    const dt = new Date(slotDate)
    dt.setHours(hours, minutes, 0, 0)
    return dt.getTime()
  }

  const upcomingBookings = bookings
    .filter((b) => {
      const slotDate = parseISO(b.slot.date)
      const [endHours, endMinutes] = b.slot.end_time.split(':').map(Number)
      const slotEndDateTime = new Date(slotDate)
      slotEndDateTime.setHours(endHours, endMinutes, 0, 0)
      return slotEndDateTime > now
    })
    .filter((b) => b.status === 'confirmed')
    .sort((a, b) => getSlotDateTime(a) - getSlotDateTime(b))

  const pastBookings = bookings
    .filter((b) => {
      const slotDate = parseISO(b.slot.date)
      const [endHours, endMinutes] = b.slot.end_time.split(':').map(Number)
      const slotEndDateTime = new Date(slotDate)
      slotEndDateTime.setHours(endHours, endMinutes, 0, 0)
      return slotEndDateTime <= now
    })
    .sort((a, b) => getSlotDateTime(b) - getSlotDateTime(a))

  const displayedBookings =
    activeTab === 'upcoming' ? upcomingBookings : pastBookings

  // Клиенту доступны только включённые планы
  const activePlans = plans.filter((p) => p.status === 'active')

  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold text-tg-text mb-4">Мои тренировки</h1>

      {/* Табы */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab('upcoming')}
          className={`flex-1 py-2 rounded-lg font-medium transition-all text-sm ${
            activeTab === 'upcoming'
              ? 'bg-tg-button text-tg-button-text'
              : 'bg-tg-secondary text-tg-text'
          }`}
        >
          Записи ({upcomingBookings.length})
        </button>
        <button
          onClick={() => setActiveTab('past')}
          className={`flex-1 py-2 rounded-lg font-medium transition-all text-sm ${
            activeTab === 'past'
              ? 'bg-tg-button text-tg-button-text'
              : 'bg-tg-secondary text-tg-text'
          }`}
        >
          История ({pastBookings.length})
        </button>
        <button
          onClick={() => setActiveTab('plans')}
          className={`flex-1 py-2 rounded-lg font-medium transition-all text-sm ${
            activeTab === 'plans'
              ? 'bg-tg-button text-tg-button-text'
              : 'bg-tg-secondary text-tg-text'
          }`}
        >
          Планы ({plans.length})
        </button>
      </div>

      {activeTab === 'upcoming' && <PairActivity onChanged={() => void loadBookings()} />}

      {/* Контент записей */}
      {(activeTab === 'upcoming' || activeTab === 'past') && (
        <>
          {/* Загрузка */}
          {isLoading && (
            <div className="text-center py-8 text-tg-hint">Загрузка...</div>
          )}

          {/* Список */}
          {!isLoading && (
            <>
              {displayedBookings.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-tg-hint mb-4">
                    {activeTab === 'upcoming'
                      ? 'Нет запланированных тренировок'
                      : 'Нет прошедших тренировок'}
                  </p>
                  {activeTab === 'upcoming' && hasTrainer && (
                    trialState === 'completed' ? (
                      <p className="text-orange-600 text-sm">
                        Пробная тренировка завершена. Ожидайте решения тренера.
                      </p>
                    ) : trialState === 'booked' ? null : (
                      <Link to="/booking">
                        <Button>{trialState === 'available' ? 'Записаться на пробную' : 'Записаться'}</Button>
                      </Link>
                    )
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {displayedBookings.map((booking) => (
                    <BookingCard
                      key={booking.id}
                      booking={booking}
                      isPast={activeTab === 'past'}
                      onCancel={() => handleCancel(booking.id)}
                    />
                  ))}
                </div>
              )}
            </>
          )}

          {/* Кнопка записи - только когда есть записи (в пустом стейте своя кнопка) */}
          {!isLoading && activeTab === 'upcoming' && hasTrainer && upcomingBookings.length > 0 && !trialState && (
            <div className="mt-6">
              <Link to="/booking">
                <Button fullWidth variant="secondary">
                  + Записаться на тренировку
                </Button>
              </Link>
            </div>
          )}
        </>
      )}

      {/* Контент планов */}
      {activeTab === 'plans' && (
        <>
          {isPlansLoading && (
            <div className="text-center py-8 text-tg-hint">Загрузка...</div>
          )}

          {!isPlansLoading && plans.length === 0 && (
            <div className="text-center py-8">
              <div className="text-4xl mb-3">📋</div>
              <p className="text-tg-hint">
                Тренер пока не назначил план
              </p>
            </div>
          )}

          {!isPlansLoading && plans.length > 0 && (
            <div className="space-y-6">
              {/* Активные планы */}
              {activePlans.length > 0 && (
                <div>
                  <h2 className="text-sm font-medium text-tg-hint uppercase tracking-wide mb-3">
                    {activePlans.length > 1 ? 'Текущие планы' : 'Текущий план'}
                  </h2>
                  <div className="grid grid-cols-1 gap-3">
                    {activePlans.map((plan) => (
                      <PlanCard
                        key={plan.id}
                        plan={plan}
                        onClick={() => setSelectedPlan(plan)}
                      />
                    ))}
                  </div>
                </div>
              )}


            </div>
          )}
        </>
      )}

      {/* Модальное окно просмотра плана */}
      {selectedPlan && (
        <PlanViewerModal
          plan={selectedPlan}
          onClose={() => setSelectedPlan(null)}
        />
      )}
    </div>
  )
}

function BookingCard({
  booking,
  isPast,
  onCancel,
}: {
  booking: Booking
  isPast: boolean
  onCancel: () => void
}) {
  const date = parseISO(booking.slot.date)
  const isBookingToday = isToday(date)

  const statusLabels: Record<string, string> = {
    confirmed: 'Подтверждено',
    cancelled: 'Отменено',
    completed: 'Завершено',
    no_show: 'Не пришёл',
  }

  const [isSendingCalendar, setIsSendingCalendar] = useState(false)

  const handleAddToCalendar = async () => {
    if (isSendingCalendar) return
    setIsSendingCalendar(true)
    try {
      await sendCalendarToTelegram(booking.id)
      alert('Файл отправлен в Telegram! Откройте чат с ботом.')
    } catch (err) {
      console.error('Error sending calendar:', err)
      alert('Не удалось отправить файл')
    } finally {
      setIsSendingCalendar(false)
    }
  }

  return (
    <div className="bg-tg-secondary rounded-xl p-4">
      <div className="flex items-center gap-4">
        <div className="text-3xl">{isPast ? '✅' : '🏋️'}</div>
        <div className="flex-1">
          <div className="font-medium text-tg-text">
            {isBookingToday ? 'Сегодня' : format(date, 'd MMMM', { locale: ru })}
          </div>
          <div className="text-sm text-tg-hint">
            {booking.slot.start_time} - {booking.slot.end_time}
          </div>
          {booking.pair_status === 'accepted' && <p className="mt-1 text-sm text-tg-button">Партнёр по тренировкам — {booking.pair_partner_name}</p>}
          {booking.status !== 'confirmed' && (
            <div className="text-xs text-tg-hint mt-1">
              {statusLabels[booking.status]}
            </div>
          )}
        </div>
        {/* Кнопки действий */}
        {!isPast && booking.status === 'confirmed' && (
          <div className="flex items-center gap-2">
            <button
              onClick={handleAddToCalendar}
              disabled={isSendingCalendar}
              className={`p-2 text-tg-button hover:bg-tg-button/10 rounded-lg transition-colors ${
                isSendingCalendar ? 'opacity-50' : ''
              }`}
              title="Добавить в календарь"
            >
              <svg
                className={`w-5 h-5 ${isSendingCalendar ? 'animate-pulse' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </button>
            <button
              onClick={onCancel}
              className="text-red-500 text-sm font-medium"
            >
              Отменить
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

function PlanCard({
  plan,
  onClick,
}: {
  plan: TrainingPlan
  onClick: () => void
}) {
  const createdDate = parseISO(plan.created_at)
  const hasImage = !!plan.image_data
  const hasExercises = plan.exercises && plan.exercises.length > 0

  return (
    <div
      onClick={onClick}
      className="bg-tg-secondary rounded-xl overflow-hidden cursor-pointer transition-transform active:scale-[0.98]"
    >
      {/* Превью изображения */}
      {hasImage && (
        <div className="aspect-video bg-tg-bg overflow-hidden">
          <img
            src={plan.image_data}
            alt={plan.title}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      {/* Информация */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-tg-text truncate">{plan.title}</h3>
            {plan.description && (
              <p className="text-sm text-tg-hint mt-1 line-clamp-2">
                {plan.description}
              </p>
            )}
          </div>
          <span className="shrink-0 px-2 py-1 bg-green-500/20 text-green-600 text-xs rounded-full">
            Активный
          </span>
        </div>

        {/* Тренер */}
        {plan.trainer_name && (
          <div className="flex items-center gap-2 mt-3 text-sm text-tg-text">
            <span className="w-5 h-5 bg-tg-button text-tg-button-text rounded-full flex items-center justify-center text-xs">
              {plan.trainer_name.charAt(0).toUpperCase()}
            </span>
            <span>{plan.trainer_name}</span>
          </div>
        )}

        {/* Мета-информация */}
        <div className="flex items-center gap-3 mt-2 text-xs text-tg-hint">
          <span>{format(createdDate, 'd MMM yyyy', { locale: ru })}</span>
          {hasExercises && (
            <span>
              {plan.exercises!.length}{' '}
              {plan.exercises!.length === 1
                ? 'упражнение'
                : plan.exercises!.length < 5
                  ? 'упражнения'
                  : 'упражнений'}
            </span>
          )}
          {hasImage && <span>📷</span>}
        </div>
      </div>
    </div>
  )
}

function PlanViewerModal({
  plan,
  onClose,
}: {
  plan: TrainingPlan
  onClose: () => void
}) {
  const [showFullImage, setShowFullImage] = useState(false)
  const hasImage = !!plan.image_data
  const hasExercises = plan.exercises && plan.exercises.length > 0
  const createdDate = parseISO(plan.created_at)

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-end sm:items-center justify-center">
      {/* Оверлей для закрытия */}
      <div className="absolute inset-0" onClick={onClose} />

      {/* Контент */}
      <div className="relative w-full sm:max-w-lg max-h-[90vh] bg-tg-bg rounded-t-2xl sm:rounded-2xl overflow-hidden flex flex-col">
        {/* Хедер */}
        <div className="flex items-center justify-between p-4 border-b border-tg-secondary">
          <div className="flex-1 min-w-0">
            <h2 className="font-semibold text-tg-text truncate">{plan.title}</h2>
            <p className="text-xs text-tg-hint">
              {format(createdDate, 'd MMMM yyyy', { locale: ru })}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-tg-hint hover:text-tg-text transition-colors"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Скроллируемый контент */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Статус и тренер */}
          <div className="flex items-center justify-between">
            <span
              className="px-3 py-1 rounded-full text-sm bg-green-500/20 text-green-600"
            >
              Активный план
            </span>
            {plan.trainer_name && (
              <div className="flex items-center gap-2 text-sm text-tg-text">
                <span className="w-6 h-6 bg-tg-button text-tg-button-text rounded-full flex items-center justify-center text-xs font-medium">
                  {plan.trainer_name.charAt(0).toUpperCase()}
                </span>
                <span>{plan.trainer_name}</span>
              </div>
            )}
          </div>

          {/* Описание */}
          {plan.description && (
            <div>
              <h3 className="text-sm font-medium text-tg-hint mb-1">
                Описание
              </h3>
              <p className="text-tg-text whitespace-pre-wrap">
                {plan.description}
              </p>
            </div>
          )}

          {/* Изображение */}
          {hasImage && (
            <div>
              <h3 className="text-sm font-medium text-tg-hint mb-2">
                Изображение
              </h3>
              <div
                onClick={() => setShowFullImage(true)}
                className="rounded-xl overflow-hidden bg-tg-secondary cursor-pointer"
              >
                <img
                  src={plan.image_data}
                  alt={plan.title}
                  className="w-full h-auto"
                />
              </div>
              <p className="text-xs text-tg-hint mt-2 text-center">
                👆 Нажмите на изображение для увеличения и скачивания
              </p>
            </div>
          )}

          {/* Таблица упражнений */}
          {hasExercises && (
            <div>
              <h3 className="text-sm font-medium text-tg-hint mb-2">
                Упражнения
              </h3>
              <div className="space-y-2">
                {plan.exercises!.map((exercise, index) => (
                  <ExerciseRow key={index} exercise={exercise} index={index} />
                ))}
              </div>
            </div>
          )}

          {/* Если нет ничего кроме названия */}
          {!hasImage && !hasExercises && !plan.description && (
            <p className="text-tg-hint text-center py-4">
              Тренер пока не добавил деталей к этому плану
            </p>
          )}
        </div>
      </div>

      {/* Полноэкранный просмотр изображения */}
      {showFullImage && hasImage && (
        <div
          className="fixed inset-0 z-[60] bg-black flex items-center justify-center p-4"
          onClick={() => setShowFullImage(false)}
        >
          <img
            src={plan.image_data}
            alt={plan.title}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          {/* Кнопка закрытия */}
          <div className="absolute top-4 right-4">
            <button
              onClick={() => setShowFullImage(false)}
              className="p-3 bg-white/20 backdrop-blur-sm rounded-full text-white hover:bg-white/30 transition-colors"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          {/* Подсказка внизу */}
          <p className="absolute bottom-4 text-white/60 text-sm">
            Нажмите на фон для закрытия
          </p>
        </div>
      )}
    </div>
  )
}

function ExerciseRow({
  exercise,
  index,
}: {
  exercise: Exercise
  index: number
}) {
  return (
    <div className="bg-tg-secondary rounded-lg p-3">
      <div className="flex items-start gap-3">
        <span className="shrink-0 w-6 h-6 bg-tg-button text-tg-button-text rounded-full flex items-center justify-center text-xs font-medium">
          {index + 1}
        </span>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-tg-text">{exercise.name}</div>

          {/* Параметры */}
          <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-sm text-tg-hint">
            {exercise.sets && <span>Подходы: {exercise.sets}</span>}
            {exercise.reps && <span>Повторения: {exercise.reps}</span>}
            {exercise.weight && <span>Вес: {exercise.weight}</span>}
            {exercise.rest && <span>Отдых: {exercise.rest}</span>}
          </div>

          {/* Заметки */}
          {exercise.notes && (
            <p className="text-sm text-tg-hint mt-2 italic">{exercise.notes}</p>
          )}
        </div>
      </div>
    </div>
  )
}
